import { useEffect, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { Spin, Empty, message } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import ProgressItem from './ProgressItem';
import CreateItem from './CreateItem';
import RepeatMeeting from './RepeatMeeting';
import { TMyMeeting } from '@/common/basis/types/database/tmy-meeting';
import { getMyMeeting } from '@/common/http/meet';
import { isSuccessCode } from '@/common/utils/response';
import { TStore } from '@/common/basis/types/tstore';
import { setIsHandBookingSuccess } from '@/common/store/booking/slice';
import { TResponse } from '@/common/basis/types/tresponse';
import { usePersonUserId } from '@/common/hooks/person-user-id';
import ModalErrorExit from '@/components/HOC/ModalErrorExit';
import MeetingDetail from '@/components/MeetingDetail';

function App() {
  const dispatch = useDispatch();
  const [isReRequest, setIsReRequest] = useState(true);
  const isHandBookingSuccess = useSelector((state: TStore.State) => state.booking.isHandBookingSuccess);
  const [createList, setCreateList] = useState<TMyMeeting.CreateItem[]>([]);
  const [progressList, setProgressList] = useState<TMyMeeting.ProgressItem[]>([]);
  const { currentUserId } = usePersonUserId();
  const isHaveProgress = useMemo(() => !!progressList.length, [progressList.length]);
  const isHaveCreate = useMemo(() => !!createList.length, [createList.length]);
  const isEmptyData = useMemo(() => !isHaveProgress && !isHaveCreate, [isHaveProgress, isHaveCreate]);
  const [isListLoading, setIsListLoading] = useState(false);
  const [isOpenRepeatMeeting, setIsOpenRepeatMeeting] = useState(false);
  const [currentCreateIndex, setCurrentCreateIndex] = useState(-1);
  const [currentProgressIndex, setCurrentProgressIndex] = useState(-1);
  const [isOpenProgressDetail, setIsOpenProgressDetail] = useState(false);
  const currentProgressItem = useMemo(() => progressList[currentProgressIndex], [currentProgressIndex, progressList]);
  const timerReRequestId = useRef<NodeJS.Timeout | null>(null);
  const { t } = useTranslation();
  const [messageApi, contextHolder] = message.useMessage({ top: 90 });
  const [isOpenErrorExit, setIsOpenErrorExit] = useState(false);
  const [exitErrorMsg, setExitErrorMsg] = useState('');

  const httpGetMyMeeting = async () => {
    if (isListLoading) {
      return;
    }
    setIsListLoading(true);
    try {
      const endTime = new Date(dayjs().add(3, 'month').toString()).getTime();
      const resp = await getMyMeeting({ endTime });

      if (isSuccessCode(resp?.code)) {
        setCreateList(resp.data.createList);
        setProgressList(resp.data.progressList);
      } else if (resp?.code === TResponse.CODE_401) {
        setExitErrorMsg(resp.msg);
      } else {
        messageApi.destroy();
        messageApi.error(resp?.msg);
      }
    } catch (err) {
      // TODO
    }
    setIsListLoading(false);
  };

  useEffect(() => {
    if (currentUserId && (isReRequest || isHandBookingSuccess)) {
      httpGetMyMeeting();
      setIsReRequest(false);
      dispatch(setIsHandBookingSuccess(false)); // 预约新建或编辑操作成功后 设置为false(关闭刷新)
    }
  }, [currentUserId, isReRequest, isHandBookingSuccess]);

  useEffect(() => {
    if (exitErrorMsg) {
      setIsOpenErrorExit(true);
    }
  }, [exitErrorMsg]);
  useEffect(() => {
    if (!isOpenProgressDetail) {
      setCurrentProgressIndex(-1);
    }
  }, [isOpenProgressDetail]);

  useEffect(() => {
    if (!isOpenErrorExit) {
      setExitErrorMsg('');
    }
  }, [isOpenErrorExit]);

  useEffect(() => {
    timerReRequestId.current = setInterval(() => setIsReRequest(true), 5 * 60 * 1000); // 5分钟刷新一次

    return () => {
      timerReRequestId.current && clearInterval(timerReRequestId.current);
      setCreateList([]);
    };
  }, []);

  useEffect(() => {
    const fn = () => setIsReRequest(true);

    window.addEventListener('focus', fn);

    return () => {
      window.removeEventListener('focus', fn);
    };
  }, []);

  return (
    <>
      {isListLoading ? (
        <div className='flex items-center justify-center'>
          <Spin className='mt-20' />
        </div>
      ) : isEmptyData ? (
        <Empty className='mt-20' description={t('noneMeeting')} />
      ) : (
        <>
          <div className='h-full overflow-y-auto'>
            <div className='p-3'>
              {isHaveProgress ? (
                <>
                  <div className='mb-3 text-left'>
                    <span className='text-[16px] font-medium text-[#131415]'>{t('inProgress')}</span>
                  </div>
                  {progressList.map((item, index) => (
                    <ProgressItem
                      index={index}
                      key={item?.roomId ?? index}
                      list={progressList}
                      setCurrentProgressIndex={setCurrentProgressIndex}
                      setIsOpenProgressDetail={setIsOpenProgressDetail}
                    />
                  ))}
                  <div className='h-4' />
                </>
              ) : (
                <></>
              )}

              {isHaveCreate ? (
                <>
                  <div className='mb-3 text-left'>
                    <span className='text-[16px] font-medium text-[#131415]'>{t('aboutToStart')}</span>
                  </div>
                  {createList.map((item, index) => (
                    <CreateItem
                      index={index}
                      key={item?.roomId ?? index}
                      list={createList}
                      setCurrentCreateIndex={setCurrentCreateIndex}
                      setIsOpenRepeatMeeting={setIsOpenRepeatMeeting}
                    />
                  ))}
                </>
              ) : (
                <></>
              )}
            </div>
          </div>
          <RepeatMeeting
            createList={createList}
            currentCreateIndex={currentCreateIndex}
            isOpenRepeatMeeting={isOpenRepeatMeeting}
            setIsOpenRepeatMeeting={setIsOpenRepeatMeeting}
          />
          <MeetingDetail
            isOpen={isOpenProgressDetail}
            roomId={currentProgressItem?.roomId ?? ''}
            roomSid={currentProgressItem?.roomSid ?? ''}
            setIsOpen={setIsOpenProgressDetail}
          />
        </>
      )}
      <ModalErrorExit isOpen={isOpenErrorExit} setIsOpen={setIsOpenErrorExit}>
        <div className='py-6 text-[14px] text-[#888]'>{exitErrorMsg}</div>
      </ModalErrorExit>
      {contextHolder}
    </>
  );
}

export default App;
